/**
 * Calendar segment computation
 *
 * Produces one or more renderable segments (`CalendarServiceItem[]`) for each
 * booking / booking_service. Floating work can yield multiple segments when
 * its duration spans shift boundaries. Exact-time work keeps its chosen clock
 * time even outside a shift (#2796), and when it starts inside a shift and
 * runs past its end, the rest continues in the next shifts (#3685).
 *
 * Unplaced floating work (`schedulingType === 'floating'` and no
 * `mechanicScheduledStart` / `startedAt`) is not a timed segment (#2686).
 *
 * Start-time priority (per placed work item):
 *   1. per-service `startedAt` — that service's actual start, always wins
 *   2. per-service `scheduledAt` — explicit user placement (drag-and-drop)
 *   3. `mechanicScheduledStart` — backend-computed shift+queue anchor
 *   4. booking-level `scheduledAt` — fallback for legacy data
 *
 * The backend's queue-cascade (`services/staff-api/src/lib/queue-cascade.ts`)
 * is responsible for advancing `mechanicScheduledStart` of queued jobs when
 * an earlier job is started or overruns, so this module trusts that value
 * and does not re-queue jobs itself.
 *
 * Planned duration (#2667 C2/C7) follows `packages/shared` planned-duration:
 * per-service materialized `estimatedDurationHours` (manual latch / labor /
 * catalog cache), including a stored zero; catalog hours only when that cache
 * is absent; booking-level cache only for no-service rows. Service rows never
 * split the booking total. Missing both cache and catalog uses 1 h.
 *
 * Lift occupancy (#2868) reuses that per-service resolver: sequential sums
 * the hours, parallel takes the longest, and a service-less row keeps the
 * booking-level cache. The Nosturit tab draws one block per booking, so that
 * span must match work-order segments and lift-overlap checks.
 */

import type {
  CalendarActualEntry,
  CalendarBooking,
  CalendarServiceAssignment,
  TentativeCalendarItem,
} from '@/hooks/useBookings';
import type { ComputedShift } from '@/hooks/useShifts';
import type { BookingStatus, CalendarServiceItem } from '@/types';
import { isTimeRangeOutsideShifts } from './outsideShift';

declare module '@/types' {
  interface CalendarServiceItem {
    /** Exact-time work that is not fully inside the mechanic's shift (#2796). */
    isOutsideShift?: boolean;
  }
}

const MS_PER_HOUR = 3_600_000;
/** Same default as C20 / shared consumers when planned hours cannot be resolved. */
const DEFAULT_PLANNED_HOURS = 1;
/** `numeric(5, 2)` maximum hundredths — matches packages/shared planned-duration. */
const MAX_PLANNED_HUNDREDTHS = 99_999;

/** Derived approval wait (#3027): alongside the status, never a capacity input. */
interface ApprovalWait {
  openApprovalCount: number | undefined;
}

function approvalWaitOf(cb: CalendarBooking): ApprovalWait | null {
  return cb.isWaitingForApproval === true ? { openApprovalCount: cb.openApprovalCount } : null;
}

/**
 * Intermediate per-work-item view used by the segmenter. Bridges the raw
 * `CalendarBooking` + `CalendarServiceAssignment` data into something the
 * segmenter can split, without React-specific concerns.
 */
interface WorkItem {
  sourceId: string;
  bookingId: string;
  workOrderNumber: string | null;
  serviceName: string;
  status: string;
  bookingStatus: BookingStatus;
  assignedMechanicId: string | null;
  mechanicName: string | null;
  vehicleRegNumber: string;
  vehicleMakeModel: string;
  customerName: string;
  customerPhone: string | null;
  liftName: string | null;
  customerWaits: boolean;
  jointInspectionRequested: boolean;
  isTentative: boolean;
  /** Booking-level scheduling type — exact appointment vs floating queue (#1139). */
  schedulingType: 'exact' | 'floating';
  durationHours: number;
  actualDurationHours: number;
  actualActiveHours: number;
  /** Derived approval wait (#3027) of the booking; carried onto every segment. */
  approval: ApprovalWait | null;
  /** Resolved start instant (priority-ordered). */
  startMs: number;
}

/**
 * Floating work with no mechanic queue placement (#2686).
 *
 * Either never placed, or recompute cleared `mechanicScheduledStart` because
 * the mechanic is unavailable open-ended. Exact-time bookings and placed
 * floating bookings stay timed segments.
 */
export interface UnplacedCalendarWork {
  bookingId: string;
  workOrderNumber: string | null;
  vehicleRegNumber: string;
  title: string;
  scheduledAt: string;
  /** Booking status; set with the approval flag so the badge skips a legacy primary status. */
  bookingStatus?: BookingStatus;
  /** Derived approval wait (#3027); set only when true. */
  isWaitingForApproval?: boolean;
  openApprovalCount?: number;
}

export interface CalendarSegmentsResult {
  segments: CalendarServiceItem[];
  unplaced: UnplacedCalendarWork[];
}

export interface BuildSegmentsInput {
  bookings: CalendarBooking[];
  tentativeItems?: TentativeCalendarItem[];
  shifts: ComputedShift[];
  /**
   * Unpaid lunch break in minutes, subtracted from each shift day's effective
   * work capacity so a full 8h shift renders ~7.5h of schedulable work instead
   * of filling the whole window (#423). Defaults to 0 (no deduction) — the
   * calendar passes the tenant setting `workdayUnpaidLunchMinutes`.
   */
  unpaidLunchMinutes?: number;
  /**
   * Whether the shift query has settled (#2796). Exact work is flagged
   * outside-shift when this is true and the assigned mechanic has no covering
   * window — including an empty list. False suppresses the flag so a pending
   * fetch cannot warn. When omitted, a non-empty `shifts` array is treated as
   * loaded.
   */
  shiftsLoaded?: boolean;
}

/**
 * Compute render-ready calendar segments and an unplaced-floating list.
 *
 * Unplaced floating bookings are excluded from timed segments so shift
 * splitting cannot pin them at opening (#2686). Pure function — no Date.now()
 * side effects; no React hooks; safe to unit-test.
 */
export function buildCalendarSegments(input: BuildSegmentsInput): CalendarSegmentsResult {
  const { bookings, tentativeItems = [], shifts, unpaidLunchMinutes = 0 } = input;
  const lunchMs = Math.max(0, unpaidLunchMinutes) * 60_000;
  const shiftIndex = indexShiftsByUser(shifts, lunchMs);
  const segments: CalendarServiceItem[] = [];
  const unplaced: UnplacedCalendarWork[] = [];

  const shiftsLoaded = resolveShiftsLoaded(input);

  for (const booking of bookings) {
    // A cancelled work order holds no time: it is neither drawn nor listed as
    // unplaced work (#3983).
    if (booking.status === 'cancelled') continue;
    const actualByService = actualEntriesOfDoneWork(booking);
    if (actualByService.size === 0 && isUnplacedFloatingBooking(booking)) {
      unplaced.push(toUnplacedCalendarWork(booking));
      continue;
    }
    for (const item of workItemsFromBooking(booking, shiftIndex, shiftsLoaded)) {
      const actual = actualByService.get(item.sourceId);
      if (actual) {
        segments.push(...actualSegments(item, actual));
      } else if (booking.scheduleIsDateOnly === true) {
        segments.push(dateOnlySegment(item, booking.scheduledAt));
      } else {
        segments.push(...segmentWorkItem(item, shiftIndex, shiftsLoaded));
      }
    }
  }

  for (const ti of tentativeItems) {
    segments.push(tentativeAsSegment(ti));
  }

  return { segments, unplaced };
}

/**
 * Floating mode with no mechanic placement and no actual start. Exact-time
 * work (including UTC-midnight legacy exact) stays a timed segment.
 */
/** Booking statuses after the mechanics' work is finished. */
const WORK_DONE_BOOKING_STATUSES: ReadonlySet<string> = new Set([
  'work_completed', 'ready_to_invoice', 'on_consolidated_invoice', 'invoiced', 'completed',
]);

/**
 * Finished services with closed clock entries, keyed by bookingServiceId.
 * Those are drawn when the work was done instead of at the plan, so finished
 * work does not linger in a future slot (#3983).
 */
function actualEntriesOfDoneWork(cb: CalendarBooking): Map<string, CalendarActualEntry[]> {
  const byService = new Map<string, CalendarActualEntry[]>();
  const bookingDone = WORK_DONE_BOOKING_STATUSES.has(cb.status);
  for (const sa of cb.serviceAssignments ?? []) {
    if (!bookingDone && sa.status !== 'completed') continue;
    const closed = (sa.actualEntries ?? []).filter((entry) => entry.endedAt !== null);
    if (closed.length > 0) byService.set(sa.bookingServiceId, closed);
  }
  return byService;
}

function actualSegments(item: WorkItem, entries: CalendarActualEntry[]): CalendarServiceItem[] {
  return [...entries]
    .sort((a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime())
    .map((entry, index) => {
      const segment = makeSegment(
        {
          ...item,
          assignedMechanicId: entry.mechanicId,
          // The planned assignee's name is wrong on someone else's entry.
          mechanicName: entry.mechanicId === item.assignedMechanicId ? item.mechanicName : null,
        },
        new Date(entry.startedAt).getTime(),
        new Date(entry.endedAt ?? entry.startedAt).getTime(),
        index,
      );
      // Every entry is real work, not a continuation of a planned block.
      segment.id = `${item.sourceId}#actual-${String(index)}`;
      segment.isContinuationSegment = false;
      segment.isActual = true;
      return segment;
    });
}

function isUnplacedFloatingBooking(cb: CalendarBooking): boolean {
  if (cb.scheduleIsDateOnly === true) return false;
  if (cb.startedAt) return false;
  if (cb.mechanicScheduledStart) return false;
  return cb.schedulingType === 'floating';
}

function toUnplacedCalendarWork(cb: CalendarBooking): UnplacedCalendarWork {
  const fromServices = cb.serviceAssignments[0]?.serviceName;
  const title = cb.serviceType || fromServices || '';
  const work: UnplacedCalendarWork = {
    bookingId: cb.id,
    workOrderNumber: cb.workOrderNumber ?? null,
    vehicleRegNumber: cb.vehicleRegNumber,
    title,
    scheduledAt: cb.scheduledAt,
  };
  const approval = approvalWaitOf(cb);
  if (approval) {
    work.bookingStatus = cb.status;
    work.isWaitingForApproval = true;
    if (approval.openApprovalCount !== undefined) work.openApprovalCount = approval.openApprovalCount;
  }
  return work;
}

// ─── Work-item extraction ──────────────────────────────────────────────────

function workItemsFromBooking(cb: CalendarBooking, shiftIndex: Map<string, ShiftWindow[]>, shiftsLoaded: boolean): WorkItem[] {
  const hasAssignments = cb.serviceAssignments && cb.serviceAssignments.length > 0;

  if (!hasAssignments) {
    // Deleting the last service clears serviceType. Do not resurrect an empty
    // work order as a legacy one-hour calendar item.
    if (!cb.serviceType) return [];
    // Legacy booking: one work item using booking-level fields.
    return [{
      sourceId: `legacy-${cb.id}`,
      bookingId: cb.id,
      workOrderNumber: cb.workOrderNumber ?? null,
      serviceName: cb.serviceType || '—',
      status: 'pending',
      bookingStatus: cb.status,
      assignedMechanicId: cb.assignedMechanicId,
      mechanicName: cb.mechanicName,
      vehicleRegNumber: cb.vehicleRegNumber,
      vehicleMakeModel: cb.vehicleMakeModel,
      customerName: cb.customerName,
      customerPhone: cb.customerPhone,
      liftName: cb.liftName,
      customerWaits: cb.customerWaits,
      jointInspectionRequested: cb.jointInspectionRequested,
      isTentative: false,
      schedulingType: cb.schedulingType ?? 'exact',
      durationHours: resolveBookingSegmentHours(cb),
      actualDurationHours: cb.actualDurationHours ?? 0,
      actualActiveHours: cb.actualActiveHours ?? 0,
      approval: approvalWaitOf(cb),
      startMs: resolveStart(cb, null),
    }];
  }

  const sorted = [...cb.serviceAssignments].sort((a, b) => a.sortOrder - b.sortOrder);

  // Service scheduling on the calendar (#544): 'parallel' starts every service
  // at the work order's anchor; 'sequential' (the default) lays them back-to-back
  // in sortOrder. Only the service's own actual start overrides that layout;
  // starting the work order must not pin all its pending services to one time.
  // Advance the cursor through shift boundaries as well as service boundaries.
  const parallel = cb.serviceScheduling === 'parallel';
  const plannedStart = cb.mechanicScheduledStart
    ? new Date(cb.mechanicScheduledStart).getTime()
    : new Date(cb.scheduledAt).getTime();
  const baseStart = cb.startedAt ? new Date(cb.startedAt).getTime()
    : Math.max(plannedStart, cb.vehicleDropoffAt ? new Date(cb.vehicleDropoffAt).getTime() : plannedStart);
  let cursorMs = baseStart;

  return sorted.map((sa) => {
    const durationHours = resolveServiceSegmentHours(sa);
    // A per-service `scheduledAt` only counts as a deliberate (drag-and-drop)
    // placement when it differs from both booking anchors. Services are created
    // with `scheduledAt` copied from the booking time, so honouring it blindly
    // would pin every service to the anchor and make the sequential/parallel
    // mode a visual no-op — which is what almost all real work orders look like
    // (#544). Treat "scheduledAt === anchor" as unplaced and let the mode govern.
    const placedMs = sa.scheduledAt ? new Date(sa.scheduledAt).getTime() : null;
    const hasDistinctPlacement = placedMs !== null
      && placedMs !== new Date(cb.scheduledAt).getTime()
      && placedMs !== (cb.mechanicScheduledStart ? new Date(cb.mechanicScheduledStart).getTime() : null);
    let startMs: number;
    if (sa.startedAt) {
      startMs = new Date(sa.startedAt).getTime();
    } else if (hasDistinctPlacement) {
      startMs = placedMs;
    } else if (parallel) {
      startMs = baseStart;
    } else {
      startMs = cursorMs;
    }
    const item: WorkItem = {
      sourceId: sa.bookingServiceId,
      bookingId: cb.id,
      workOrderNumber: cb.workOrderNumber ?? null,
      serviceName: sa.serviceName,
      status: sa.status,
      bookingStatus: cb.status,
      assignedMechanicId: sa.assignedMechanicId,
      mechanicName: sa.mechanicName,
      vehicleRegNumber: cb.vehicleRegNumber,
      vehicleMakeModel: cb.vehicleMakeModel,
      customerName: cb.customerName,
      customerPhone: cb.customerPhone,
      liftName: cb.liftName,
      customerWaits: cb.customerWaits,
      jointInspectionRequested: cb.jointInspectionRequested,
      isTentative: false,
      schedulingType: cb.schedulingType ?? 'exact',
      durationHours,
      actualDurationHours: sa.actualDurationHours ?? 0,
      actualActiveHours: sa.actualActiveHours ?? 0,
      approval: approvalWaitOf(cb),
      startMs,
    };
    if (!parallel) {
      const segments = segmentWorkItem(item, shiftIndex, shiftsLoaded);
      const end = segments.at(-1)?.scheduledEnd;
      if (end) cursorMs = Math.max(cursorMs, new Date(end).getTime());
    }
    return item;
  });
}

/**
 * Parse planned hours the same way as `parseDurationHours` in
 * `packages/shared/src/scheduling/planned-duration.ts`. Portal apps must not
 * import `packages/` (`docs/frontend-package-boundaries.md`). Zero is a value;
 * missing / invalid is null.
 */
function parsePlannedHours(value: unknown): number | null {
  if (value == null) return null;

  let raw: string | number;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    raw = trimmed;
  } else if (typeof value === 'number') {
    raw = value;
  } else {
    return null;
  }

  const parsed = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0) return null;

  const hundredths = Math.round(Number((parsed * 100).toFixed(4)));
  if (hundredths > MAX_PLANNED_HUNDREDTHS) return null;
  return hundredths / 100;
}

/**
 * Per-service planned hours: materialized cache (manual / labor / catalog),
 * then catalog, then 1 h. Never divides the booking total (#2667 C2).
 */
function resolveServiceSegmentHours(sa: CalendarServiceAssignment): number {
  const cached = parsePlannedHours(sa.estimatedDurationHours);
  if (cached != null) return cached;
  const catalog = parsePlannedHours(sa.catalogDurationHours);
  if (catalog != null) return catalog;
  return DEFAULT_PLANNED_HOURS;
}

/** No-service booking: booking-level planned cache, else 1 h. */
function resolveBookingSegmentHours(cb: CalendarBooking): number {
  const hours = parsePlannedHours(cb.estimatedDurationHours);
  return hours != null ? hours : DEFAULT_PLANNED_HOURS;
}

/**
 * Occupied hours of a booking on a lift (#2868).
 *
 * Each service is per-service cache → catalog → 1 h. Sequential sums those
 * hours; parallel takes the longest. A booking without services keeps its own
 * planned cache, else 1 h. The booking-level total is ignored when services
 * exist — it is often a stale sequential sum and would over-occupy parallel
 * work.
 */
export function resolveBookingOccupancyHours(cb: CalendarBooking): number {
  const assignments = cb.serviceAssignments;
  if (!assignments || assignments.length === 0) {
    return resolveBookingSegmentHours(cb);
  }

  const hours = assignments.map(resolveServiceSegmentHours);
  if (cb.serviceScheduling === 'parallel') {
    return hours.reduce((longest, h) => (h > longest ? h : longest), 0);
  }

  let totalHundredths = 0;
  for (const h of hours) {
    totalHundredths += Math.round(Number((h * 100).toFixed(4)));
  }
  return totalHundredths / 100;
}

/**
 * Instant range a booking occupies on the lift calendar (#2868).
 *
 * Start is the booking-level anchor (`startedAt`, then `mechanicScheduledStart`,
 * then `scheduledAt`) — the same instant sequential/parallel layout uses.
 * End is start plus occupancy hours so one lift block matches the latest
 * work-order segment when services share that anchor.
 */
export function resolveBookingOccupancyRange(cb: CalendarBooking): {
  startMs: number;
  endMs: number;
  hours: number;
} {
  const hours = resolveBookingOccupancyHours(cb);
  const startMs = occupancyAnchorMs(cb);
  return {
    startMs,
    endMs: startMs + Math.max(0, hours) * MS_PER_HOUR,
    hours,
  };
}

function occupancyAnchorMs(cb: CalendarBooking): number {
  if (cb.startedAt) return new Date(cb.startedAt).getTime();
  if (cb.mechanicScheduledStart) return new Date(cb.mechanicScheduledStart).getTime();
  return new Date(cb.scheduledAt).getTime();
}

/**
 * Pick the start instant for a work item, following the documented priority.
 * Per-service `scheduledAt` is honored over booking-level anchors so manual
 * drag-and-drop placements persist.
 */
function resolveStart(cb: CalendarBooking, sa: CalendarServiceAssignment | null): number {
  if (cb.startedAt) return new Date(cb.startedAt).getTime();
  if (sa?.scheduledAt) return new Date(sa.scheduledAt).getTime();
  const planned = new Date(cb.mechanicScheduledStart ?? cb.scheduledAt).getTime();
  return Math.max(planned, cb.vehicleDropoffAt ? new Date(cb.vehicleDropoffAt).getTime() : planned);
}

// ─── Segment splitting ─────────────────────────────────────────────────────

type ShiftWindow = { startMs: number; endMs: number; rawEndMs: number };

/**
 * Split a work item by the assigned mechanic's shift boundaries.
 *
 * Exact-time work (#2796) starts at the chosen clock time and is never jumped
 * to another shift; when it starts inside a shift and runs past its end, the
 * rest continues in the next shifts (#3685). Floating work still consumes
 * remaining duration shift-by-shift. If no shifts apply (unassigned, or no
 * shifts on the day), the item is emitted as a single segment of its full
 * duration.
 */
function segmentWorkItem(
  item: WorkItem,
  shiftIndex: Map<string, ShiftWindow[]>,
  shiftsLoaded: boolean,
): CalendarServiceItem[] {
  const totalMs = Math.max(0, item.durationHours * MS_PER_HOUR);
  const fallbackEnd = item.startMs + totalMs;
  const userShifts = item.assignedMechanicId
    ? shiftIndex.get(item.assignedMechanicId)
    : undefined;

  if (item.schedulingType === 'exact') {
    const continued = continueExactAcrossShifts(item, totalMs, userShifts);
    if (continued) return continued;
    const outside = exactWorkIsOutsideShift(item, fallbackEnd, userShifts, shiftsLoaded);
    return [makeSegment(item, item.startMs, fallbackEnd, 0, outside)];
  }

  if (!item.assignedMechanicId) {
    return [makeSegment(item, item.startMs, fallbackEnd, 0)];
  }

  if (!userShifts || userShifts.length === 0) {
    return [makeSegment(item, item.startMs, fallbackEnd, 0)];
  }

  const segments: CalendarServiceItem[] = [];
  let remainingMs = totalMs;
  let cursor = item.startMs;
  let segmentIndex = 0;

  // Iterate windows that can still contain part of the remaining duration.
  // Sorted ascending so we naturally advance through the day.
  for (const window of userShifts) {
    if (remainingMs <= 0) break;
    if (window.endMs <= cursor) continue; // shift already past

    // If there's a gap before the next shift, jump the cursor forward.
    if (cursor < window.startMs) cursor = window.startMs;

    const available = window.endMs - cursor;
    if (available <= 0) continue;

    const consume = Math.min(available, remainingMs);
    const segEnd = cursor + consume;
    segments.push(makeSegment(item, cursor, segEnd, segmentIndex));
    segmentIndex += 1;
    remainingMs -= consume;
    cursor = segEnd;
  }

  if (remainingMs > 0) {
    // No further shift available — emit the remainder as a single trailing
    // segment so the work is still visible on the calendar. Acceptance criteria
    // explicitly cover "päivä ilman seuraavaa työvuoroa".
    segments.push(makeSegment(item, cursor, cursor + remainingMs, segmentIndex));
  }

  if (segments.length === 0) {
    // Defensive: every work item must render at least once. Falls back to the
    // unsegmented window.
    segments.push(makeSegment(item, item.startMs, fallbackEnd, 0));
  }

  return segments;
}

/**
 * Exact-time work that starts inside a shift but runs past its end continues
 * at the start of the mechanic's next shifts (#3685): 8 h from 15:00 in a
 * shift ending 18:00 is 15–18 today and 07–12 on the next workday, not one
 * block into the evening. The chosen start never moves (#2796). Returns null —
 * one block at the chosen time, flagged when outside the shift — when the work
 * fits, starts outside any shift, or the loaded shifts cannot take the rest.
 */
function continueExactAcrossShifts(
  item: WorkItem,
  totalMs: number,
  userShifts: ShiftWindow[] | undefined,
): CalendarServiceItem[] | null {
  if (!item.assignedMechanicId || !userShifts || userShifts.length === 0) return null;
  const first = userShifts.findIndex((w) => w.startMs <= item.startMs && item.startMs < w.endMs);
  if (first < 0) return null;
  if (item.startMs + totalMs <= (userShifts[first]?.endMs ?? 0)) return null;

  const segments: CalendarServiceItem[] = [];
  let remainingMs = totalMs;
  let cursor = item.startMs;
  for (const window of userShifts.slice(first)) {
    if (remainingMs <= 0) break;
    if (window.endMs <= cursor) continue;
    if (cursor < window.startMs) cursor = window.startMs;
    const consume = Math.min(window.endMs - cursor, remainingMs);
    if (consume <= 0) continue;
    segments.push(makeSegment(item, cursor, cursor + consume, segments.length));
    remainingMs -= consume;
    cursor += consume;
  }
  return remainingMs > 0 ? null : segments;
}

function resolveShiftsLoaded(input: BuildSegmentsInput): boolean {
  if (input.shiftsLoaded === false) return false;
  if (input.shiftsLoaded === true) return true;
  return input.shifts.length > 0;
}

function exactWorkIsOutsideShift(
  item: WorkItem,
  endMs: number,
  userShifts: ShiftWindow[] | undefined,
  shiftsLoaded: boolean,
): boolean {
  if (!item.assignedMechanicId || !shiftsLoaded) return false;
  const windows = (userShifts ?? []).map((window) => ({
    startMs: window.startMs,
    endMs: window.rawEndMs,
  }));
  return isTimeRangeOutsideShifts(item.startMs, endMs, windows);
}

function makeSegment(
  item: WorkItem,
  startMs: number,
  endMs: number,
  segmentIndex: number,
  isOutsideShift = false,
): CalendarServiceItem {
  const seg: CalendarServiceItem = {
    id: `${item.sourceId}#${segmentIndex}`,
    sourceId: item.sourceId,
    bookingId: item.bookingId,
    workOrderNumber: item.workOrderNumber,
    scheduledStart: new Date(startMs).toISOString(),
    scheduledEnd: new Date(endMs).toISOString(),
    serviceName: item.serviceName,
    vehicleRegNumber: item.vehicleRegNumber,
    vehicleMakeModel: item.vehicleMakeModel,
    customerName: item.customerName,
    customerPhone: item.customerPhone,
    assignedMechanicId: item.assignedMechanicId,
    status: item.status,
    bookingStatus: item.bookingStatus,
    liftName: item.liftName,
    customerWaits: item.customerWaits,
    jointInspectionRequested: item.jointInspectionRequested,
    durationHours: (endMs - startMs) / MS_PER_HOUR,
    plannedHours: item.durationHours,
    actualDurationHours: item.actualDurationHours,
    actualActiveHours: item.actualActiveHours,
    mechanicName: item.mechanicName,
    schedulingType: item.schedulingType,
    segmentIndex,
    isContinuationSegment: segmentIndex > 0,
  };
  if (item.isTentative) seg.isTentative = true;
  if (item.approval) {
    seg.isWaitingForApproval = true;
    if (item.approval.openApprovalCount !== undefined) {
      seg.openApprovalCount = item.approval.openApprovalCount;
    }
  }
  if (isOutsideShift) seg.isOutsideShift = true;
  return seg;
}

/**
 * One segment covering the whole local day of `scheduledAt` (#2760).
 *
 * Imported history often knows only the day. Shift splitting would pin it to
 * shift start and stack every such job there, which reads as history that
 * never happened. `durationHours` keeps the work's hours rather than the 24 h
 * span, so utilization still books them on that day.
 */
function dateOnlySegment(item: WorkItem, scheduledAt: string): CalendarServiceItem {
  const at = new Date(scheduledAt);
  const dayStart = new Date(at.getFullYear(), at.getMonth(), at.getDate());
  const dayEnd = new Date(at.getFullYear(), at.getMonth(), at.getDate() + 1);
  const seg = makeSegment(item, dayStart.getTime(), dayEnd.getTime(), 0);
  // The API sends numeric columns as strings ("2.00"); coerce before it is summed.
  seg.durationHours = Number(item.durationHours);
  seg.isDateOnly = true;
  return seg;
}

// ─── Shift indexing ────────────────────────────────────────────────────────

/**
 * Build a per-user list of shift windows expressed as absolute epoch ms,
 * sorted ascending. The shifts API returns `{date, startTime, endTime}` as
 * local-wallclock strings; we combine them with `new Date(...)` so callers
 * (e.g. JSDOM tests in UTC) get a deterministic ordering relative to the
 * resolved work-item `startMs`, which uses the same interpretation.
 *
 * `lunchMs` reserves an unpaid break at the end of each shift day by shrinking
 * the usable window (#423). It only applies when the shift is longer than the
 * break itself, so short shifts keep their full length and capacity never goes
 * negative.
 */
function indexShiftsByUser(shifts: ComputedShift[], lunchMs = 0): Map<string, ShiftWindow[]> {
  const map = new Map<string, ShiftWindow[]>();
  for (const sh of shifts) {
    const startMs = new Date(`${sh.date}T${sh.startTime}`).getTime();
    const rawEndMs = new Date(`${sh.date}T${sh.endTime}`).getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(rawEndMs) || rawEndMs <= startMs) continue;
    const endMs = rawEndMs - startMs > lunchMs ? rawEndMs - lunchMs : rawEndMs;
    const list = map.get(sh.userId) ?? [];
    list.push({ startMs, endMs, rawEndMs });
    map.set(sh.userId, list);
  }
  for (const list of map.values()) {
    list.sort((a, b) => a.startMs - b.startMs);
  }
  return map;
}

// ─── Tentative items ───────────────────────────────────────────────────────

function tentativeAsSegment(ti: TentativeCalendarItem): CalendarServiceItem {
  const startMs = new Date(ti.scheduledAt).getTime();
  const durationHours = ti.estimatedDurationHours ?? 1;
  const endMs = startMs + durationHours * MS_PER_HOUR;
  return {
    id: `${ti.id}#0`,
    sourceId: ti.id,
    bookingId: ti.bookingId,
    workOrderNumber: null,
    scheduledStart: new Date(startMs).toISOString(),
    scheduledEnd: new Date(endMs).toISOString(),
    serviceName: ti.serviceType || '—',
    vehicleRegNumber: ti.vehicleRegNumber,
    vehicleMakeModel: ti.vehicleMakeModel,
    customerName: ti.customerName,
    customerPhone: null,
    assignedMechanicId: ti.assignedMechanicId,
    status: ti.status,
    bookingStatus: ti.status as BookingStatus,
    liftName: null,
    customerWaits: false,
    jointInspectionRequested: false,
    durationHours,
    plannedHours: durationHours,
    actualDurationHours: 0,
    actualActiveHours: 0,
    mechanicName: ti.mechanicName,
    isTentative: true,
    segmentIndex: 0,
    isContinuationSegment: false,
  };
}
